import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUpload, FiPackage, FiStar, FiDownload, FiSettings, FiLogOut, FiEdit, FiTrash2, FiCode, FiUser, FiMessageSquare, FiHeart, FiArrowRight, FiBookmark } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [extensions, setExtensions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalExtensions: 0,
    totalDownloads: 0,
    avgRating: 0,
    pending: 0,
    reviewsCount: 0,
    savedExtensions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const role = localStorage.getItem('userRole') || 'user';

    if (!token || !userId) {
      toast.error('Please login to access dashboard');
      navigate('/login');
      return;
    }

    setUserRole(role);
    fetchUserData(userId, token, role);
  }, [navigate]);

  const fetchUserData = async (userId, token, role) => {
    try {
      setLoading(true);

      // Fetch user profile
      const userResponse = await axios.get('https://extension.avalw.com/api/users/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userResponse.data.data || userResponse.data);

      if (role === 'developer') {
        // Fetch developer's own extensions (all statuses)
        const extensionsResponse = await axios.get('https://extension.avalw.com/api/extensions/my', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userExtensions = extensionsResponse.data.data || [];
        setExtensions(userExtensions);

        // Calculate developer stats
        const totalDownloads = userExtensions.reduce((sum, ext) => sum + (ext.downloadCount || 0), 0);
        const totalRatings = userExtensions.reduce((sum, ext) => sum + (ext.rating || 0), 0);
        const avgRating = userExtensions.length > 0 ? (totalRatings / userExtensions.length).toFixed(1) : 0;
        const pending = userExtensions.filter(ext => ext.status === 'pending').length;

        setStats({
          totalExtensions: userExtensions.length,
          totalDownloads,
          avgRating,
          pending,
          reviewsCount: 0,
          savedExtensions: 0
        });
      } else {
        // Fetch user's reviews
        try {
          const reviewsResponse = await axios.get(`https://extension.avalw.com/api/reviews/user/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setReviews(reviewsResponse.data.data || reviewsResponse.data || []);
        } catch (e) {
          setReviews([]);
        }

        setStats({
          totalExtensions: 0,
          totalDownloads: 0,
          avgRating: 0,
          pending: 0,
          reviewsCount: reviews.length,
          savedExtensions: 0
        });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDeleteExtension = async (extensionId) => {
    if (!window.confirm('Are you sure you want to delete this extension?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://extension.avalw.com/api/extensions/${extensionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Extension deleted successfully');
      const userId = localStorage.getItem('userId');
      fetchUserData(userId, token, userRole);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete extension');
    }
  };

  const handleSubmitExtension = async (extensionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://extension.avalw.com/api/extensions/${extensionId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Extension submitted for approval!');
      const userId = localStorage.getItem('userId');
      fetchUserData(userId, token, userRole);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit extension');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  // USER DASHBOARD
  if (userRole === 'user') {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="mb-12 flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2 text-gray-900">My Account</h1>
              <p className="text-gray-600">Welcome back, {user?.username}!</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-900 rounded-lg hover:border-gray-900 transition-all"
              >
                <FiSettings />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-900 rounded-lg hover:border-gray-900 transition-all font-medium"
              >
                <FiLogOut />
                Logout
              </button>
            </div>
          </div>

          {/* User Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <StatCard icon={<FiMessageSquare />} label="Reviews Written" value={reviews.length} />
            <StatCard icon={<FiHeart />} label="Favorites" value={stats.savedExtensions} />
            <StatCard icon={<FiBookmark />} label="Collections" value="0" />
          </div>

          {/* Upgrade to Developer CTA */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-2xl p-8 mb-12 text-white cursor-pointer"
            onClick={() => navigate('/register/dev')}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Become a Developer</h2>
                <p className="text-gray-300 mb-4">Want to publish your own extensions for Cronos Browser?</p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Publish unlimited extensions</li>
                  <li>• Access to developer tools & analytics</li>
                  <li>• Extension Builder tool</li>
                </ul>
              </div>
              <div className="hidden md:flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-medium">
                Upgrade Now <FiArrowRight />
              </div>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <ActionCard
              title="Browse Extensions"
              description="Discover new extensions for Cronos Browser"
              icon={<FiPackage className="text-4xl" />}
              action="Explore"
              onClick={() => navigate('/')}
            />
            <ActionCard
              title="My Settings"
              description="Update your profile and preferences"
              icon={<FiUser className="text-4xl" />}
              action="Settings"
              onClick={() => navigate('/settings')}
            />
          </div>

          {/* User's Reviews */}
          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Your Reviews</h2>
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <FiMessageSquare className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">You haven't written any reviews yet</p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium"
                >
                  Browse Extensions
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <ReviewRow key={review.id} review={review} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DEVELOPER DASHBOARD
  return (
    <div className="min-h-screen bg-white py-12">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-12 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-gray-900">Developer Dashboard</h1>
            <p className="text-gray-600">Welcome back, {user?.username}!</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-900 rounded-lg hover:border-gray-900 transition-all"
            >
              <FiSettings />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-900 rounded-lg hover:border-gray-900 transition-all font-medium"
            >
              <FiLogOut />
              Logout
            </button>
          </div>
        </div>

        {/* Developer Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <StatCard icon={<FiPackage />} label="Extensions" value={stats.totalExtensions} />
          <StatCard icon={<FiDownload />} label="Total Downloads" value={stats.totalDownloads} />
          <StatCard icon={<FiStar />} label="Avg Rating" value={stats.avgRating || '-'} />
          <StatCard icon={<FiSettings />} label="Pending Review" value={stats.pending} />
        </div>

        {/* Developer Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <ActionCard
            title="Build New Extension"
            description="Create a new extension with our visual builder"
            icon={<FiCode className="text-4xl" />}
            action="Start Building"
            onClick={() => navigate('/build')}
          />
          <ActionCard
            title="Documentation"
            description="Learn how to build AVALW extensions"
            icon={<FiPackage className="text-4xl" />}
            action="View Docs"
            onClick={() => navigate('/docs')}
          />
        </div>

        {/* Extensions List */}
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Your Extensions</h2>
          {extensions.length === 0 ? (
            <div className="text-center py-12">
              <FiPackage className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">You haven't created any extensions yet</p>
              <button
                onClick={() => navigate('/build')}
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium flex items-center gap-2 mx-auto"
              >
                <FiCode /> Build Your First Extension
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {extensions.map(extension => (
                <ExtensionRow
                  key={extension.id}
                  extension={extension}
                  onDelete={handleDeleteExtension}
                  onSubmit={handleSubmitExtension}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl p-6 border-2 border-gray-200"
    >
      <div className="text-gray-900 mb-4 text-2xl">{icon}</div>
      <div className="text-3xl font-bold mb-2 text-gray-900">{value}</div>
      <div className="text-gray-600 text-sm">{label}</div>
    </motion.div>
  );
}

function ActionCard({ title, description, icon, action, onClick }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className="bg-white rounded-2xl p-8 border-2 border-gray-200 cursor-pointer group"
    >
      <div className="text-gray-900 mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2 group-hover:text-gray-600 transition-colors text-gray-900">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      <button className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all">
        {action}
      </button>
    </motion.div>
  );
}

function ExtensionRow({ extension, onDelete, onSubmit }) {
  const statusColors = {
    approved: 'text-green-600 bg-green-100 border-green-200',
    pending: 'text-yellow-600 bg-yellow-100 border-yellow-200',
    rejected: 'text-red-600 bg-red-100 border-red-200',
    draft: 'text-gray-600 bg-gray-100 border-gray-200'
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all border border-gray-200">
      <div className="flex items-center gap-4">
        {extension.icon ? (
          <img src={extension.icon} alt={extension.name} className="w-12 h-12 rounded-lg object-cover" />
        ) : (
          <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
            <FiPackage className="text-white text-xl" />
          </div>
        )}
        <div>
          <h4 className="font-semibold text-gray-900">{extension.name}</h4>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>v{extension.version || '1.0.0'}</span>
            <span>{extension.downloadCount || 0} downloads</span>
            <span><FiStar className="inline" /> {extension.rating || '-'}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className={`px-4 py-1 rounded-full text-sm font-medium border ${statusColors[extension.status] || statusColors.draft}`}>
          {extension.status || 'draft'}
        </div>
        {extension.status === 'draft' && (
          <>
            <Link
              to={`/edit/${extension.id}`}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
              title="Edit extension"
            >
              <FiEdit />
            </Link>
            <button
              onClick={() => onSubmit(extension.id)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              title="Submit for approval"
            >
              Submit
            </button>
          </>
        )}
        <a
          href={`https://extension.avalw.com/api/extensions/${extension.id}/download`}
          className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-all"
          title="Download .avalw"
        >
          <FiDownload />
        </a>
        <button
          onClick={() => onDelete(extension.id)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
          title="Delete extension"
        >
          <FiTrash2 />
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ review }) {
  const stars = '★'.repeat(review.rating || 0) + '☆'.repeat(5 - (review.rating || 0));

  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
        <FiPackage className="text-white text-xl" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-semibold text-gray-900">{review.extensionName || 'Extension'}</h4>
          <span className="text-yellow-500">{stars}</span>
        </div>
        <p className="text-gray-600 text-sm">{review.comment || review.content}</p>
        <p className="text-gray-400 text-xs mt-2">
          {new Date(review.createdAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

export default Dashboard;
